import { BaseSetting, TemsWebSetting } from "@/Settings/SettingsManager"
import DataStreamManagerObj from "./DataStreamManager"
import SiteManagerView from "@/Site/SiteManagerView"
import Graph2D, { ScatterDataMode } from "@/Graph2D"
import SliderControl from "./SliderControl"
import PositionControl from "./PositionControl"
import { LocationViewModel, DeviceViewModel, DeviceDataPointViewModel, DataFileViewModel, CalibrationRequest, MeshTransform, Polygon3DViewModel, MeshCubeInfo } from "@/TemsManagerWebInterface"
import TemsWebEvents from "@/TemsWebEvents"
import Localization from "@/Localization"
import ManagerView from "@/ManagerView";
import THREE from "three"

class CalibrationAssistantSettings extends BaseSetting {
    useBoxFilter = true;
    numberOfProfiles = 100;
    iterations = 100;
    filterDistance = 0.2;

    limitPosX = 0.1;
    limitPosY = 0.1;
    limitPosZ = 0.1;
    limitAngleX = 10.0;
    limitAngleY = 10.0;
    limitAngleZ = 10.0;

    boxMinX = 0;
    boxMaxX = 30.0;
    boxMinY = 0.2;
    boxMaxY = 3.0;
    boxMinZ = -1.0;
    boxMaxZ = 1.0;
}

export default class CalibrationAssistant {

    private static Settings = new TemsWebSetting<CalibrationAssistantSettings>("CalibrationAssistant", new CalibrationAssistantSettings());

    private calibratableDeviceIds = [];
    private objectDropdownLut = {};
    private objectDropdownLutNameToIdx = {};
    private objectDropdownSelections = [];

    public get calibrationObjectId() {
        return "6342367a-f0fd-4759-" + this.roadModelId.substr(0, 4) + "-" + this.roadModelId.substr(4, 12); //"-bb23-d0eaa07cb3ca"; // This is a predefined GUID for the CalibrationObject, set in RoadTypeLogic
    };

    public boxFilterMeshId = "boxFilter";

    private domContainer: JQuery;
    private dataStreamManager: DataStreamManagerObj;
    private siteManagerView: SiteManagerView; // TODO would be nice to get rid of this thing

    private calibrationView: Graph2D;
    private calibrationObjectPositionControl: PositionControl;

    private boxFilterXMin: SliderControl;
    private boxFilterYMin: SliderControl;
    private boxFilterZMin: SliderControl;
    private boxFilterXMax: SliderControl;
    private boxFilterYMax: SliderControl;
    private boxFilterZMax: SliderControl;

    private locationViewModel: LocationViewModel;
    private deviceModel: DeviceViewModel;
    private dataPoint: DeviceDataPointViewModel;
    private selectedCalibrationObject: string;

    private isRunning = false;
    private calibrationRun: string = null;

    public get IsRunning() {
        return this.isRunning;
    }

    public get IsCalibratable() {
        return this.isRunning && !!this.deviceModel && !!this.locationViewModel && !!this.dataPoint;
    }

    public get roadModelId() {
        return this.locationViewModel ? this.locationViewModel.Id : "";
    }

    constructor(dom: JQuery, dataStreamManager: DataStreamManagerObj, siteManagerView: SiteManagerView) {
        this.domContainer = dom;
        this.dataStreamManager = dataStreamManager;
        this.siteManagerView = siteManagerView;

        this.calibrationView = new Graph2D("#calibrationAssistant2dGraph");

        var self = this;
        $("#calibrationAssistant2dGraph").on("resized", () => {
            this.calibrationView.initScaleAndCenter(6);
            if (this.IsCalibratable) {
                this.updateReferencePolygon();
            }
        });

        TemsWebEvents.subscribeEvent(TemsWebEvents.DevicePositionChanged, (evt, deviceId) => {
            if (self.IsCalibratable && (deviceId === self.deviceModel.Id || deviceId === self.calibrationObjectId)) {
                self.updateReferencePolygon();
            }
        });

        TemsWebEvents.subscribeEvent(TemsWebEvents.DeviceSensorAssignmentChanged, (evt, deviceId) => {
            if (self.IsCalibratable && (deviceId === self.deviceModel.Id)) {
                self.updateReferencePolygon();
            }
        });

        TemsWebEvents.subscribeEvent(TemsWebEvents.DataStreamEnabled, (evt, dataPointViewModel: DeviceDataPointViewModel) => {
            if (self.IsCalibratable) {
                setTimeout(() => self.updateReferencePolygon(), 50); // little bit hacky.. becasue self is called as it is toggling...
            }
        });

        TemsWebEvents.subscribeEvent(TemsWebEvents.DataStreamDisabled, (evt, dataPointViewModel: DeviceDataPointViewModel) => {
            if (self.isRunning && !!self.dataPoint && dataPointViewModel.DataPointId === self.dataPoint.DataPointId) {
                self.close();
            }
        });

        TemsWebEvents.subscribeEvent(TemsWebEvents.SceneLoaded, (evt) => {
            if (self.isRunning) {
                self.updateBoxFilter();
            }
        });

        TemsWebEvents.subscribeEvent(TemsWebEvents.ElementSelected, (evt) => {
            if (self.isRunning) {
                self.updateBoxFilter();
            }

            self.updateVisibility();
        });

        TemsWebEvents.subscribeEvent(TemsWebEvents.SiteStateChanged, (evt) => {
            if (self.isRunning) {
                self.close();
            }
        });

        TemsWebEvents.subscribeEvent(TemsWebEvents.CalibratableDevice, (evt, datastream: DeviceDataPointViewModel) => {
            if (self.calibratableDeviceIds.indexOf(datastream.DeviceConfigId) < 0) {
                self.calibratableDeviceIds.push(datastream.DeviceConfigId); // yes that thing grows, 
            }

            this.notifyCalibratable();
        });

        $("#buttonCalibrationAssistantCalibrate").click(() => this.calibrate());
        $("#buttonCalibrationAssistantCalibrateCancel").click(() => {
            this.calibrationRun = null;
            this.updateVisibility();
        });

        $("#calibrationAssistantNumberOfProfiles").editable({
            value: CalibrationAssistant.Settings.Current.numberOfProfiles,
            success: (resp, newValue) => CalibrationAssistant.Settings.updateSettings(s => s.numberOfProfiles = Number(newValue))
        });

        $("#calibrationAssistantIterations").editable({
            value: CalibrationAssistant.Settings.Current.iterations,
            success: (resp, newValue) => CalibrationAssistant.Settings.updateSettings(s => s.iterations = Number(newValue))
        });

        $("#calibrationAssistantFilterDistance").editable({
            value: CalibrationAssistant.Settings.Current.filterDistance,
            success: (resp, newValue) => {
                CalibrationAssistant.Settings.updateSettings(s => s.filterDistance = Number(newValue));
                this.updateReferencePolygon();
            }
        });

        $("#checkboxCalibrationAssistantEnableBoxFilter").change(() => {
            CalibrationAssistant.Settings.updateSettings(s => s.useBoxFilter = $("#checkboxCalibrationAssistantEnableBoxFilter").is(":checked"));
            this.updateBoxFilter();
        });

        $("#checkboxCalibrationAssistantEnableBoxFilter").prop('checked', CalibrationAssistant.Settings.Current.useBoxFilter);
    }

    public notifyCalibratable() {
        TemsWebEvents.publishEvent(TemsWebEvents.CalibratableDevicesFound, [this.canCalibrate]);
    }

    private get canCalibrate(): boolean {
        for (var i = 0; i < this.calibratableDeviceIds.length; i++) {
            const compactDeviceId = this.calibratableDeviceIds[i].replace(new RegExp("-", 'g'), "");
            const deviceObject = this.siteManagerView.getObjectFromSiteById(compactDeviceId);
            if (deviceObject && (deviceObject as DeviceViewModel).IsEnabled) {
                return true;
            }
        }

        return false;
    }

    public show() {
        if (!this.canCalibrate) {
            console.error("Show called but no devices to calibrate");
            return;
        }

        this.locationViewModel = undefined;
        this.deviceModel = undefined;
        this.dataPoint = undefined;

        this.isRunning = true;

        // build calibratableDevice selection
        var devices = [];
        var deviceLut = {}
        for (var i = 0; i < this.calibratableDeviceIds.length; i++) {
            const deviceId = this.calibratableDeviceIds[i];
            const compactDeviceId = deviceId.replace(new RegExp("-", 'g'), "");
            const deviceObject = this.siteManagerView.getObjectFromSiteById(compactDeviceId);
            if (deviceObject && (deviceObject as DeviceViewModel).IsEnabled) {
                devices.push({ value: i, text: this.siteManagerView.findRoadIdByDeviceId(compactDeviceId).Title + " - " + (deviceObject as DeviceViewModel).Title });
                deviceLut["device" + i] = deviceObject;
            }
        }

        if (devices.length <= 0) {
            this.siteManagerView.showAlertMessage(
                Localization.resourceStrings.CalibrationAssistant_MissingStlFilesTitle,
                Localization.resourceStrings.CalibrationAssistant_MissingStlFilesMessage);
            this.cleanup();
            return;
        }

        $("#calibrationAssistantDeviceDropdown").editable('destroy');
        $("#calibrationAssistantDeviceDropdown")
            .editable({
                source: devices,
                success: (response, newValue) => {
                    if (newValue) {
                        this.deviceSelected(deviceLut["device" + newValue]);
                    }
                }
            });

        $("#calibrationAssistantDeviceDropdown").editable('setValue', null);

        // Get Files and populate dropdown with possible STL Files
        $.getJSON("/api/DataFileManager/GetAllFiles", (newDataFileList: DataFileViewModel[]) => {
            var stlFiles = newDataFileList.filter(dataFile => dataFile.TypeTitle.indexOf("STL") >= 0); //.map(dataFile => dataFile.Name); // proper would be to identify by the TypeId
            if (stlFiles.length === 0) {
                // Problem: What if configured file gets deleted?          
                //                    this.siteManagerView.showAlertMessage(
                //                        Localization.resourceStrings.CalibrationAssistant_MissingCalibratableDevicesTitle,
                //                        Localization.resourceStrings.CalibrationAssistant_MissingCalibratableDevicesMessage);
                this.cleanup();
            } else {
                $("#calibrationAssistantObjectDropdown").editable('destroy');
                $("#calibrationAssistantObjectDropdown").html("");

                this.objectDropdownLut = {};
                this.objectDropdownSelections = stlFiles.map((v, idx) => {
                    this.objectDropdownLut["" + idx] = v;
                    this.objectDropdownLutNameToIdx[v.Name] = idx;
                    return { value: idx, text: v.Name };
                });

                $("#calibrationAssistantObjectDropdown").editable({
                    source: this.objectDropdownSelections,
                    success: (response, newValue) => {
                        const calibrationObject = this.objectDropdownLut["" + newValue];
                        this.selectedCalibrationObject = calibrationObject ? calibrationObject.Name : undefined;

                        $.ajax({
                            url: `/api/SiteEdit/SaveCalibrationConfig`,
                            method: "POST",
                            data: {
                                RoadId: this.locationViewModel.Id,
                                CalibrationConfig: {
                                    CalibrationObject: calibrationObject ? calibrationObject.Name : "",
                                    Orientation: this.calibrationObjectPositionControl.Orientation
                                }
                            }
                        }).done(() => {
                            TemsWebEvents.publishEvent(TemsWebEvents.CalibrationObjectChanged);
                            this.updateReferencePolygon();

                            // show the values depending on the calibration object being valid
                            if (newValue) {
                                $("#calibrationAssistantSettings").slideDown();
                            } else {
                                $("#calibrationAssistantSettings").slideUp();
                            }
                        }).fail(() => {
                            $("#calibrationAssistantSettings").slideUp();
                        });
                    }
                });
            }
        }).fail((jqXhr, textStatus, errorThrown) => {
            console.log("Error: " + errorThrown);
        });

        this.updateVisibility();
    }

    private deviceSelected(deviceModel: DeviceViewModel) {
        this.deviceModel = deviceModel;
        this.locationViewModel = this.siteManagerView.findRoadIdByDeviceId(deviceModel.Id);
        this.dataPoint = undefined;

        if (this.locationViewModel.CalibrationConfig) {
            this.selectedCalibrationObject = this.locationViewModel.CalibrationConfig.CalibrationObject;
        }

        // make sure the boxFilterMesh doesn't exist anymore
        this.siteManagerView.siteView.getScene().removeMeshById(this.boxFilterMeshId);

        // populate next dropdown
        var dataStreamSelections = [];
        var dataStreamLut = {};
        var dsLen = deviceModel.DeviceDataPoints.length;
        for (let i = 0; i < dsLen; i++) {
            const dataStream = deviceModel.DeviceDataPoints[i];
            // If the stream is a ProfilePolar3D, and the Device movable, we attach the Calibration Assistant
            if (dataStream.DataFormat === "Ectn.Utilities.ProfilePolar3D") {
                dataStreamSelections.push({ value: i, text: dataStream.Title });
                dataStreamLut[`datapoint${i}`] = dataStream;
            }
        }

        var self = this;
        $("#calibrationAssistantDatapointDropdown").editable("destroy");
        $("#calibrationAssistantDatapointDropdown").editable({
            value: null,
            type: "select",
            source: dataStreamSelections,
            success: (response, newValue) => {
                if (newValue) {
                    self.dataPointSelected(dataStreamLut[`datapoint${newValue}`]);
                }
            }
        });
        $("#calibrationAssistantDatapointDropdown").editable('setValue', null);

        // Box Filter Control
        const sumLanes = this.locationViewModel.Lanes.length > 0 
            ? this.locationViewModel.Lanes.map(l => l.Width).reduce((p, c) => p + c) 
            : this.locationViewModel.Width;
        const boundingBox = new THREE.Vector3(sumLanes, 5, 12);

        this.boxFilterXMin = new SliderControl("Min X", -5.0, boundingBox.x + 5.0, 0.001, "m", (id, newValue) => { this.updateBoxFilter(); }, "calibrationBoxFilter", `calibration-assistant-boxfilter-min-x`, true);
        this.boxFilterYMin = new SliderControl("Min Y", -5.0, boundingBox.y + 5.0, 0.001, "m", (id, newValue) => { this.updateBoxFilter(); }, "calibrationBoxFilter", `calibration-assistant-boxfilter-min-y`, true);
        this.boxFilterZMin = new SliderControl("Min Z", -boundingBox.z - 5.0, boundingBox.z + 5.0, 0.001, "m", (id, newValue) => { this.updateBoxFilter(); }, "calibrationBoxFilter", `calibration-assistant-boxfilter-min-z`, true);
        this.boxFilterXMax = new SliderControl("Max X", -5.0, boundingBox.x + 5.0, 0.001, "m", (id, newValue) => { this.updateBoxFilter(); }, "calibrationBoxFilter", `calibration-assistant-boxfilter-max-x`, true);
        this.boxFilterYMax = new SliderControl("Max Y", -5.0, boundingBox.y + 5.0, 0.001, "m", (id, newValue) => { this.updateBoxFilter(); }, "calibrationBoxFilter", `calibration-assistant-boxfilter-max-y`, true);
        this.boxFilterZMax = new SliderControl("Max Z", -boundingBox.z - 5.0, boundingBox.z + 5.0, 0.001, "m", (id, newValue) => { this.updateBoxFilter(); }, "calibrationBoxFilter", `calibration-assistant-boxfilter-max-z`, true);

        $("#calibrationBoxFilterSettings")
            .html("")
            .append($("<div>", { style: "display:inline-block; width: 50%;" })
                .append(this.boxFilterXMin.Content)
                .append(this.boxFilterYMin.Content)
                .append(this.boxFilterZMin.Content))
            .append($("<div>", { style: "display:inline-block; width: 50%;" })
                .append(this.boxFilterXMax.Content)
                .append(this.boxFilterYMax.Content)
                .append(this.boxFilterZMax.Content));

        // Load and adjust box settings
        var boxMinX = CalibrationAssistant.Settings.Current.boxMinX;
        var boxMinY = Math.max(CalibrationAssistant.Settings.Current.boxMinY, this.boxFilterYMin.rangeMin);
        var boxMinZ = Math.max(CalibrationAssistant.Settings.Current.boxMinZ, this.boxFilterZMin.rangeMin);
        var boxMaxX = CalibrationAssistant.Settings.Current.boxMaxX;
        var boxMaxY = Math.min(CalibrationAssistant.Settings.Current.boxMaxY, this.boxFilterYMax.rangeMax);
        var boxMaxZ = Math.min(CalibrationAssistant.Settings.Current.boxMaxZ, this.boxFilterZMax.rangeMax);
        if (boxMinX < this.boxFilterXMin.rangeMin) {
            boxMinX = 0.0;
        }

        if (boxMaxX > this.boxFilterXMax.rangeMax) {
            boxMaxX = boundingBox.x;
        }

        this.boxFilterXMin.setValue(boxMinX, Localization.formatNumber3Digits);
        this.boxFilterYMin.setValue(boxMinY, Localization.formatNumber3Digits);
        this.boxFilterZMin.setValue(boxMinZ, Localization.formatNumber3Digits);
        this.boxFilterXMax.setValue(boxMaxX, Localization.formatNumber3Digits);
        this.boxFilterYMax.setValue(boxMaxY, Localization.formatNumber3Digits);
        this.boxFilterZMax.setValue(boxMaxZ, Localization.formatNumber3Digits);

        this.siteManagerView.siteView.updateMeshTransform(this.calibrationObjectId, this.locationViewModel.CalibrationConfig.Orientation);

        // Calibration Object Position Control
        if (this.calibrationObjectPositionControl) {
            this.calibrationObjectPositionControl.destroy();
        }
        $("#calibrationObjectOrientationControl").html("");
        this.calibrationObjectPositionControl = new PositionControl({
            Id: this.calibrationObjectId,
            OrientationRanges: []
        }, this.siteManagerView);
        $("#calibrationObjectOrientationControl").append(this.calibrationObjectPositionControl.Content);

        this.calibrationObjectPositionControl.updateOrientationRanges([{
            MaxAngleX: 180,
            MaxAngleY: 180,
            MaxAngleZ: 180,
            MaxCoordinateX: sumLanes + 5.0,
            MaxCoordinateY: 10,
            MaxCoordinateZ: 30,
            MinAngleX: -180,
            MinAngleY: -180,
            MinAngleZ: -180,
            MinCoordinateX: -5,
            MinCoordinateY: -2,
            MinCoordinateZ: -30
        }]);

        this.updateVisibility();
    }

    private dataPointSelected(dataPoint: DeviceDataPointViewModel) {
        this.dataPoint = dataPoint;
        this.dataStreamManager.attachGraph(this.calibrationView);

        // if datapoint is not active; 
        if (this.dataStreamManager.findActiveDataPointIndex(dataPoint.DeviceConfigId, dataPoint.DataPointId) < 0) {
            TemsWebEvents.publishEvent(TemsWebEvents.ToggleDataStream, [dataPoint]);
        }

        this.updateVisibility(); // make everything visible

        //// Setup 2D View
        // Determine if XY or ZY Plane, we don't support arbitrary planes at the moment...
        this.calibrationView.resize();
        const deviceMesh = this.siteManagerView.siteView.getScene().getObject3DFromMeshId(this.deviceModel.Id);
        let rot: THREE.Euler = new THREE.Euler();
        let rotMat: THREE.Matrix4 = new THREE.Matrix4();
        deviceMesh.getWorldRotation(rot);
        deviceMesh.matrixWorld.extractRotation(rotMat);

        let scanPlaneNormal = new THREE.Vector3(0, 0, 1);   // Take the default scanPlaneNormal (xy plane with normal in +z)
        scanPlaneNormal.applyEuler(deviceMesh.rotation);    // rotate it like the device
        let angle = Math.abs(scanPlaneNormal.angleTo(new THREE.Vector3(0, 0, 1)));
        if (angle > Math.PI * 0.25 && angle < Math.PI * 0.75) {
            this.calibrationView.setZY();
        } else {
            this.calibrationView.setXY();
        }

        this.calibrationView.initScaleAndCenter(6);

        if (this.locationViewModel.CalibrationConfig.CalibrationObject) {
            const idx = this.objectDropdownLutNameToIdx[this.locationViewModel.CalibrationConfig.CalibrationObject];
            if (idx >= 0) {
                $("#calibrationAssistantObjectDropdown").editable("setValue", idx);
            }
        } else {
            $("#calibrationAssistantObjectDropdown").editable("setValue", null);
        }

        // adjust the sliders
        this.calibrationObjectPositionControl.forceSliderUpdate();
    }

    private calibrationObjectSelected() {
        const obj3D = this.siteManagerView.siteView.getScene().getObject3DFromMeshId(this.calibrationObjectId);
        if (obj3D) {
            this.calibrationObjectPositionControl.updateFromMesh(obj3D, false);
        }

        this.updateReferencePolygon();
        this.updateBoxFilter();
        this.updateVisibility();
    }

    public close() {
        $("#calibrationAssistantDiv").slideUp(400, () => this.cleanup());
    }

    public calibrate() {
        // Box Filter
        const minX = this.boxFilterXMin.getValue();
        const minY = this.boxFilterYMin.getValue();
        const minZ = this.boxFilterZMin.getValue();
        const maxX = this.boxFilterXMax.getValue();
        const maxY = this.boxFilterYMax.getValue();
        const maxZ = this.boxFilterZMax.getValue();

        // Advanced options
        const calibrationRequest: CalibrationRequest = {
            RoadId: this.locationViewModel.Id,
            BoxMinCoords: CalibrationAssistant.Settings.Current.useBoxFilter ? [minX, minY, minZ] : null,
            BoxMaxCoords: CalibrationAssistant.Settings.Current.useBoxFilter ? [maxX, maxY, maxZ] : null,
            CalibrationIterations: Number($("#calibrationAssistantIterations").editable("getValue", true)),
            FilterDistance: CalibrationAssistant.Settings.Current.filterDistance,
            CalibrationProfileCount: Number($("#calibrationAssistantNumberOfProfiles").editable("getValue", true)),
            DeviceDataIdentifier: {
                DataFormat: this.dataPoint.DataFormat,
                DataPointId: this.dataPoint.DataPointId,
                DeviceId: this.deviceModel.Id,
                IsCompressed: this.dataPoint.IsCompressed,
                MaxHeight: 10,
                MaxWidth: 10
            }
        };

        const calibrationRun = THREE.Math.generateUUID();
        this.calibrationRun = calibrationRun;

        $.ajax({
            url: `/api/Calibration/Calibrate`,
            method: "POST",
            data: calibrationRequest
        }).done((response: MeshTransform) => {
            if (calibrationRun === this.calibrationRun) {
                this.calibrationRun = null;
                this.updateVisibility();
                this.showAcceptDialog(response);
            }
        }).fail(err => {
            this.calibrationRun = null;
            $("#calibrationErrorMessageText").text(err.responseJSON.Message);
            $("#calibrationErrorMessage").show();
            this.updateVisibility();
        });

        this.updateVisibility();
    }

    private showAcceptDialog(newPositionA: MeshTransform) {
        const newPosition = JSON.parse(JSON.stringify(newPositionA));
        var elemAcknowledge = $("#modalAcceptCalibration");
        elemAcknowledge.on("shown.bs.modal", () => { });

        var acceptButton = elemAcknowledge.find("#modalAcceptCalibrationButton");
        acceptButton.unbind("click");
        acceptButton.on("click", () => this.acceptOptimization(newPosition));

        const oldPosition = this.siteManagerView.siteView.getDevicePosition(this.deviceModel.Id);

        (document.getElementById("acceptCalibrationPosXApply") as HTMLInputElement).checked = true;
        (document.getElementById("acceptCalibrationPosYApply") as HTMLInputElement).checked = true;
        (document.getElementById("acceptCalibrationPosZApply") as HTMLInputElement).checked = true;
        (document.getElementById("acceptCalibrationAngleXApply") as HTMLInputElement).checked = true;
        (document.getElementById("acceptCalibrationAngleYApply") as HTMLInputElement).checked = true;
        (document.getElementById("acceptCalibrationAngleZApply") as HTMLInputElement).checked = true;

        document.getElementById("acceptCalibrationPosXOld").innerText = Localization.formatNumber3Digits(oldPosition.PositionX) + " m";
        document.getElementById("acceptCalibrationPosYOld").innerText = Localization.formatNumber3Digits(oldPosition.PositionY) + " m";
        document.getElementById("acceptCalibrationPosZOld").innerText = Localization.formatNumber3Digits(oldPosition.PositionZ) + " m";
        document.getElementById("acceptCalibrationAngleXOld").innerText = Localization.formatNumber1Digit(oldPosition.AngleX / Math.PI * 180.0) + " °";
        document.getElementById("acceptCalibrationAngleYOld").innerText = Localization.formatNumber1Digit(oldPosition.AngleY / Math.PI * 180.0) + " °";
        document.getElementById("acceptCalibrationAngleZOld").innerText = Localization.formatNumber1Digit(oldPosition.AngleZ / Math.PI * 180.0) + " °";

        document.getElementById("acceptCalibrationPosXNew").innerText = Localization.formatNumber3Digits(newPosition.PositionX) + " m";
        document.getElementById("acceptCalibrationPosYNew").innerText = Localization.formatNumber3Digits(newPosition.PositionY) + " m";
        document.getElementById("acceptCalibrationPosZNew").innerText = Localization.formatNumber3Digits(newPosition.PositionZ) + " m";
        document.getElementById("acceptCalibrationAngleXNew").innerText = Localization.formatNumber1Digit(newPosition.AngleX / Math.PI * 180.0) + " °";
        document.getElementById("acceptCalibrationAngleYNew").innerText = Localization.formatNumber1Digit(newPosition.AngleY / Math.PI * 180.0) + " °";
        document.getElementById("acceptCalibrationAngleZNew").innerText = Localization.formatNumber1Digit(newPosition.AngleZ / Math.PI * 180.0) + " °";

        const limitPosX = CalibrationAssistant.Settings.Current.limitPosX;
        const limitPosY = CalibrationAssistant.Settings.Current.limitPosY;
        const limitPosZ = CalibrationAssistant.Settings.Current.limitPosZ;
        const limitAngleX = CalibrationAssistant.Settings.Current.limitAngleX;
        const limitAngleY = CalibrationAssistant.Settings.Current.limitAngleY;
        const limitAngleZ = CalibrationAssistant.Settings.Current.limitAngleZ;

        $("#acceptCalibrationPosXLimit").editable({
            value: Localization.formatNumber3Digits(limitPosX),
            success: (resp, newValue) => {
                CalibrationAssistant.Settings.updateSettings(s => s.limitPosX = Localization.parseLocaleNumber(newValue));
                this.acceptDialogDangerHighlights(newPosition);
            }
        });

        $("#acceptCalibrationPosYLimit").editable({
            value: Localization.formatNumber3Digits(limitPosY),
            success: (resp, newValue) => {
                CalibrationAssistant.Settings.updateSettings(s => s.limitPosY = Localization.parseLocaleNumber(newValue));
                this.acceptDialogDangerHighlights(newPosition);
            }
        });

        $("#acceptCalibrationPosZLimit").editable({
            value: Localization.formatNumber3Digits(limitPosZ),
            success: (resp, newValue) => {
                CalibrationAssistant.Settings.updateSettings(s => s.limitPosZ = Localization.parseLocaleNumber(newValue));
                this.acceptDialogDangerHighlights(newPosition);
            }
        });

        $("#acceptCalibrationAngleXLimit").editable({
            value: Localization.formatNumber3Digits(limitAngleX),
            success: (resp, newValue) => {
                CalibrationAssistant.Settings.updateSettings(s => s.limitAngleX = Localization.parseLocaleNumber(newValue));
                this.acceptDialogDangerHighlights(newPosition);
            }
        });

        $("#acceptCalibrationAngleYLimit").editable({
            value: Localization.formatNumber3Digits(limitAngleY),
            success: (resp, newValue) => {
                CalibrationAssistant.Settings.updateSettings(s => s.limitAngleY = Localization.parseLocaleNumber(newValue));
                this.acceptDialogDangerHighlights(newPosition);
            }
        });

        $("#acceptCalibrationAngleZLimit").editable({
            value: Localization.formatNumber3Digits(limitAngleZ),
            success: (resp, newValue) => {
                CalibrationAssistant.Settings.updateSettings(s => s.limitAngleZ = Localization.parseLocaleNumber(newValue));
                this.acceptDialogDangerHighlights(newPosition);
            }
        });

        this.acceptDialogDangerHighlights(newPosition);

        // Enable/Disable ignores based on limits..
        (document.getElementById("acceptCalibrationPosXApply") as HTMLInputElement).checked = !$("#acceptCalibrationPosXRow").hasClass("danger");
        (document.getElementById("acceptCalibrationPosYApply") as HTMLInputElement).checked = !$("#acceptCalibrationPosYRow").hasClass("danger");
        (document.getElementById("acceptCalibrationPosZApply") as HTMLInputElement).checked = !$("#acceptCalibrationPosZRow").hasClass("danger");
        (document.getElementById("acceptCalibrationAngleXApply") as HTMLInputElement).checked = !$("#acceptCalibrationAngleXRow").hasClass("danger");
        (document.getElementById("acceptCalibrationAngleYApply") as HTMLInputElement).checked = !$("#acceptCalibrationAngleYRow").hasClass("danger");
        (document.getElementById("acceptCalibrationAngleZApply") as HTMLInputElement).checked = !$("#acceptCalibrationAngleZRow").hasClass("danger");

        elemAcknowledge.modal(ManagerView.MODAL_SHOW_BACKDROP);
    }

    private acceptDialogDangerHighlights(newPosition: MeshTransform) {
        const oldPosition = this.siteManagerView.siteView.getDevicePosition(this.deviceModel.Id);

        const diffPosX = newPosition.PositionX - oldPosition.PositionX;
        const diffPosY = newPosition.PositionY - oldPosition.PositionY;
        const diffPosZ = newPosition.PositionZ - oldPosition.PositionZ;
        const diffAngleX = this.minDiffBetweenAngles(newPosition.AngleX, oldPosition.AngleX);
        const diffAngleY = this.minDiffBetweenAngles(newPosition.AngleY, oldPosition.AngleY);
        const diffAngleZ = this.minDiffBetweenAngles(newPosition.AngleZ, oldPosition.AngleZ);

        document.getElementById("acceptCalibrationPosXDiff").innerText = Localization.formatNumber3Digits(diffPosX) + " m";
        document.getElementById("acceptCalibrationPosYDiff").innerText = Localization.formatNumber3Digits(diffPosY) + " m";
        document.getElementById("acceptCalibrationPosZDiff").innerText = Localization.formatNumber3Digits(diffPosZ) + " m";
        document.getElementById("acceptCalibrationAngleXDiff").innerText = Localization.formatNumber1Digit(diffAngleX / Math.PI * 180.0) + " °";
        document.getElementById("acceptCalibrationAngleYDiff").innerText = Localization.formatNumber1Digit(diffAngleY / Math.PI * 180.0) + " °";
        document.getElementById("acceptCalibrationAngleZDiff").innerText = Localization.formatNumber1Digit(diffAngleZ / Math.PI * 180.0) + " °";

        $("#acceptCalibrationPosXRow").toggleClass("danger", Math.abs(diffPosX) > CalibrationAssistant.Settings.Current.limitPosX);
        $("#acceptCalibrationPosYRow").toggleClass("danger", Math.abs(diffPosY) > CalibrationAssistant.Settings.Current.limitPosY);
        $("#acceptCalibrationPosZRow").toggleClass("danger", Math.abs(diffPosZ) > CalibrationAssistant.Settings.Current.limitPosZ);
        $("#acceptCalibrationAngleXRow").toggleClass("danger", Math.abs(diffAngleX / Math.PI * 180.0) > CalibrationAssistant.Settings.Current.limitAngleX);
        $("#acceptCalibrationAngleYRow").toggleClass("danger", Math.abs(diffAngleY / Math.PI * 180.0) > CalibrationAssistant.Settings.Current.limitAngleY);
        $("#acceptCalibrationAngleZRow").toggleClass("danger", Math.abs(diffAngleZ / Math.PI * 180.0) > CalibrationAssistant.Settings.Current.limitAngleZ);

        // X-Editable has this very nice behavior to store the old background-color... and reapply it inside the style attribute
        document.getElementById("acceptCalibrationPosXLimit").style.removeProperty("background-color");
        document.getElementById("acceptCalibrationPosYLimit").style.removeProperty("background-color");
        document.getElementById("acceptCalibrationPosZLimit").style.removeProperty("background-color");
        document.getElementById("acceptCalibrationAngleXLimit").style.removeProperty("background-color");
        document.getElementById("acceptCalibrationAngleYLimit").style.removeProperty("background-color");
        document.getElementById("acceptCalibrationAngleZLimit").style.removeProperty("background-color");
    }

    private minDiffBetweenAngles(newAngle: number, oldAngle: number) {
        let res = newAngle - oldAngle;
        res += (res > Math.PI) ? -2 * Math.PI : (res < -Math.PI) ? 2 * Math.PI : 0;
        return res;
    }

    private updateReferencePolygon() {
        $.ajax({
            url: `/api/Calibration/ReferencePolygon?roadId=${this.locationViewModel.Id}&deviceId=${this.deviceModel.Id}`,
            method: "POST"
        }).done((response: Polygon3DViewModel) => {
            // clear all!
            this.calibrationView.removeSeries("calibobjseries");
            this.calibrationView.removeSeries("calibobjseriesfield");
            this.calibrationView.removeSeries("calibobjseriespoints");

            if (response.Coordinates && response.Coordinates.length > 0) {
                var points = [];
                while (response.Coordinates.length > 0) {
                    const tripplet = response.Coordinates.splice(0, 3);
                    points.push({
                        x: tripplet[0],
                        y: tripplet[1],
                        z: tripplet[2]
                    });
                }

                // Let's draw the next Picasso! :D

                // actual polygon
                this.calibrationView.renderScatterData({
                    SeriesId: "calibobjseries",
                    SeriesName: "Calibration Object",
                    Color: "rgba(200,0,0,1.0)",
                    Data: points
                }, ScatterDataMode.Stretch);

                // region around filter distance
                this.calibrationView.renderScatterData({
                    SeriesId: "calibobjseriesfield",
                    SeriesName: "Calibration Object",
                    Color: "rgba(200,128,128,1.0)", // yes, transparency would be nice, BUT it will double up around the points
                    Data: points,
                    ScaleStrokeWidth: CalibrationAssistant.Settings.Current.filterDistance * 2.0
                }, ScatterDataMode.Stretch);

                // Just the points!
                this.calibrationView.renderScatterData({
                    SeriesId: "calibobjseriespoints",
                    SeriesName: "Calibration Object",
                    Color: "rgba(255,0,0,1.0)",
                    Data: points
                }, ScatterDataMode.Points);

                // also update the boxfilter...
                this.updateBoxFilter();

                $("#buttonCalibrationAssistantCalibrate").prop("disabled", false);
                $("#calibrationErrorMessage").hide();
            } else {
                $("#calibrationErrorMessageText").text(Localization.resourceStrings.CalibrationAssistant_ErrorNoReferencePolygon);
                $("#calibrationErrorMessage").show();
                $("#buttonCalibrationAssistantCalibrate").prop("disabled", true);
            }
        }).fail(err => {
            $("#calibrationErrorMessageText").text(Localization.resourceStrings.CalibrationAssistant_ErrorNoReferencePolygon);
            $("#calibrationErrorMessage").show();
            $("#buttonCalibrationAssistantCalibrate").prop("disabled", true);
        });
    }

    public cleanup() {
        this.isRunning = false;
        if (this.siteManagerView.siteView.hasScene()) {
            this.siteManagerView.siteView.getScene().removeMeshById(this.boxFilterMeshId);
        }
        
        this.dataStreamManager.detachGraph(this.calibrationView);

        this.updateVisibility();
    }

    private updateBoxFilter() {
        if (CalibrationAssistant.Settings.Current.useBoxFilter) {
            const minX = this.boxFilterXMin.getValue();
            const minY = this.boxFilterYMin.getValue();
            const minZ = this.boxFilterZMin.getValue();
            const maxX = this.boxFilterXMax.getValue();
            const maxY = this.boxFilterYMax.getValue();
            const maxZ = this.boxFilterZMax.getValue();

            // Save current input for reload
            CalibrationAssistant.Settings.updateSettings(current => {
                current.boxMinX = minX;
                current.boxMinY = minY;
                current.boxMinZ = minZ;
                current.boxMaxX = maxX;
                current.boxMaxY = maxY;
                current.boxMaxZ = maxZ;
            });

            if (minX >= maxX || minY >= maxY || minZ >= maxZ) {
                // ALERT
            }

            // update mesh
            const filterBoxModel: MeshCubeInfo = {
                Id: this.boxFilterMeshId,
                IsVisible: true,
                Transform: {
                    PositionX: (maxX + minX) * 0.5,
                    PositionY: (maxY + minY) * 0.5,
                    PositionZ: (maxZ + minZ) * 0.5,
                    AngleX: 0,
                    AngleY: 0,
                    AngleZ: 0,
                    ScaleX: 1.0,
                    ScaleY: 1.0,
                    ScaleZ: 1.0
                },
                Color: -16777216,
                Opacity: 0.15,
                RequiredStates: [],
                Shader: undefined,
                SizeX: maxX - minX,
                SizeY: maxY - minY,
                SizeZ: maxZ - minZ,
                IsSelectable: false,
                IsMovable: false
            };

            // does it exist?
            const dummyMesh = this.siteManagerView.siteView.getScene().getObject3DFromMeshId(this.boxFilterMeshId);
            if (!dummyMesh) {
                const roadMesh = this.siteManagerView.siteView.getScene().getObject3DFromMeshId(this.locationViewModel.Id);
                this.siteManagerView.siteView.getScene().addDummyElement(this.boxFilterMeshId, 100000.0, false, roadMesh);
            }

            this.siteManagerView.siteView.getScene().updateMesh(filterBoxModel, true);

            // update graphs
            this.calibrationView.renderCube("boxfiltercube",
                { x: minX, y: minY, z: minZ },
                { x: maxX, y: maxY, z: maxZ },
                { "fill": "rgba(159,221,236, 0.25)" }
            );
        } else {
            // BoxFilterDisabled
            this.siteManagerView.siteView.getScene().removeMeshById(this.boxFilterMeshId);
            this.calibrationView.removeSeries("boxfiltercube");
        }

        this.updateVisibility();
    }

    private acceptOptimization(meshTransform: MeshTransform): void {
        if ((document.getElementById("acceptCalibrationPosXApply") as HTMLInputElement).checked) {
            this.siteManagerView.siteView.updateMeshPositionX(this.deviceModel.Id, meshTransform.PositionX);
        }

        if ((document.getElementById("acceptCalibrationPosYApply") as HTMLInputElement).checked) {
            this.siteManagerView.siteView.updateMeshPositionY(this.deviceModel.Id, meshTransform.PositionY);
        }

        if ((document.getElementById("acceptCalibrationPosZApply") as HTMLInputElement).checked) {
            this.siteManagerView.siteView.updateMeshPositionZ(this.deviceModel.Id, meshTransform.PositionZ);
        }

        if ((document.getElementById("acceptCalibrationAngleXApply") as HTMLInputElement).checked) {
            this.siteManagerView.siteView.updateMeshRotationX(this.deviceModel.Id, meshTransform.AngleX / Math.PI * 180.0);
        }

        if ((document.getElementById("acceptCalibrationAngleYApply") as HTMLInputElement).checked) {
            this.siteManagerView.siteView.updateMeshRotationY(this.deviceModel.Id, meshTransform.AngleY / Math.PI * 180.0);
        }

        if ((document.getElementById("acceptCalibrationAngleZApply") as HTMLInputElement).checked) {
            this.siteManagerView.siteView.updateMeshRotationZ(this.deviceModel.Id, meshTransform.AngleZ / Math.PI * 180.0);
        }
    }


    private updateVisibility() {
        $("#calibrationAssistantDiv").toggle(this.isRunning);
        $("#calibartionAssistantDataPointSelect").toggle(!!this.deviceModel);
        $("#calibartionAssistantCalibrationObjectSelect").toggle(!!this.dataPoint);

        $("#calibrationAssistantControls")
            .toggle(
            !!this.deviceModel &&
            !!this.dataPoint &&
            this.dataStreamManager.findActiveDataPointIndex(this.dataPoint.DeviceConfigId, this.dataPoint.DataPointId) >= 0);

        $("#calibrationAssistantSettings").toggle(!!this.selectedCalibrationObject);
        $("#calibrationBoxFilterSettings").toggle(CalibrationAssistant.Settings.Current.useBoxFilter);

        this.siteManagerView.siteView.updateMeshGeneric(this.calibrationObjectId, (mesh) => {
            mesh.visible = this.isRunning && !!this.dataPoint;
        });

        $("#buttonCalibrationAssistantCalibrate").toggle(this.calibrationRun === null);
        $("#buttonCalibrationAssistantCalibrateCancel").toggle(this.calibrationRun !== null);
    }
}

